import React, { useLayoutEffect, useRef, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { useProviderStatus } from '@/shared/hooks/useProviderStatus';
import { textoAviso, TEXTO_NO_VERIFICADO } from './mockModeMessage';
import { publicarAltoAvisoDemo } from './avisos-superiores';

/**
 * gap-mock-mode-banner — BLOCKER legal/comercial.
 *
 * Persistent yellow banner shown across the dashboard whenever a capability
 * is not live. Without it, usuario ve "factura pagada" o "NCF emitido"
 * creyendo que es real.
 *
 * - Yellow (warning) per spec.
 * - GH#887: el texto se arma por CAPACIDAD (mockModeMessage.ts), no es fijo; si
 *   la consulta de estado falla dice que no pudo verificar, no afirma «simulado».
 * - Dismissible per-session ONLY (sessionStorage, NOT localStorage —
 *   banner must re-appear on next browser session so el riesgo nunca
 *   queda olvidado).
 * - GH#2318: es `fixed`, así que NO empuja nada por sí solo y tapaba la cabecera (logo, menú, campana,
 *   avatar). Publica su alto real en una variable CSS (avisos-superiores.ts) y MainLayout lo suma al
 *   `top` de la cabecera y de la barra lateral y al relleno del contenido. El texto no se toca.
 */
const SESSION_DISMISS_KEY = 'mock-mode-banner-dismissed';

const MockModeBanner: React.FC = () => {
  const { status, loading, error } = useProviderStatus();
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return sessionStorage.getItem(SESSION_DISMISS_KEY) === '1';
  });
  const avisoRef = useRef<HTMLDivElement | null>(null);

  // Si la consulta fallo NO se afirma «simulado»: se dice que no se pudo verificar.
  const texto = loading || dismissed ? null : status ? textoAviso(status) : error ? TEXTO_NO_VERIFICADO : null;
  const visible = texto !== null;

  // GH#2318: mientras el aviso se ve, su alto real queda publicado (y se actualiza si cambia: el texto
  // se corta distinto por ancho); al ocultarse o desmontarse vuelve a 0 y la cabecera sube.
  useLayoutEffect(() => {
    const el = avisoRef.current;
    if (!visible || !el) {
      publicarAltoAvisoDemo(0);
      return undefined;
    }
    const medir = () => publicarAltoAvisoDemo(el.getBoundingClientRect().height);
    medir();
    if (typeof ResizeObserver === 'undefined') {
      return () => publicarAltoAvisoDemo(0);
    }
    const observador = new ResizeObserver(medir);
    observador.observe(el);
    return () => {
      observador.disconnect();
      publicarAltoAvisoDemo(0);
    };
  }, [visible]);

  if (!texto) {
    return null;
  }

  const handleDismiss = () => {
    sessionStorage.setItem(SESSION_DISMISS_KEY, '1');
    setDismissed(true);
  };

  return (
    <div
      ref={avisoRef}
      role="alert"
      aria-live="polite"
      className="fixed top-0 left-0 right-0 z-[120] bg-yellow-300 text-yellow-950 border-b border-yellow-500 shadow-sm"
    >
      <div className="flex items-center justify-between px-4 py-2 gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <p className="text-xs sm:text-sm font-semibold truncate">
            {texto.titulo}
            <span className="hidden sm:inline font-normal ml-2 opacity-90">
              {texto.detalle}
            </span>
          </p>
        </div>
        <button
          onClick={handleDismiss}
          aria-label="Ocultar aviso modo demo"
          className="flex-shrink-0 p-1 rounded hover:bg-yellow-400/60 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default MockModeBanner;
